/**
 * Midway Authentication for SigV4 API Gateway calls.
 *
 * Flow:
 * 1. Check if id_token is in URL params (returning from Midway redirect)
 * 2. If not, try cross-site /SSO call (works if 3rd-party cookies allowed)
 * 3. If that fails, redirect to Midway /SSO/redirect which returns with ?id_token=...
 * 4. Use token to call STS AssumeRoleWithWebIdentity
 * 5. Sign API calls with the resulting credentials
 */

const ROLE_ARN = 'arn:aws:iam::910639348312:role/BMR-MidwayApiTempCredsRole';
const REGION = 'us-west-2';
const MIDWAY_HOST = 'midway-auth.amazon.com';
const STS_ENDPOINT = `https://sts.${REGION}.amazonaws.com`;
const CLIENT_ID = 'integ-metrics-insight-ai.aft.amazon.dev';

export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: Date;
}

let cachedCredentials: AwsCredentials | null = null;
let cachedToken: string | null = null;
let tokenExpiry: number = 0;

function generateNonce(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(Array.from(array).map(b => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '').substring(0, 64);
}

/**
 * Check if there's a token in the URL (from Midway redirect).
 * If found, clean the URL and return the token.
 */
function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('id_token');
  if (token) {
    // Clean up URL - remove id_token and state params
    const url = new URL(window.location.href);
    url.searchParams.delete('id_token');
    url.searchParams.delete('state');
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    return token;
  }
  return null;
}

/**
 * Build the Midway SSO URL for cross-site token fetch
 */
function buildSSOUrl(): string {
  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: CLIENT_ID,
    redirect_uri: window.location.origin + window.location.pathname,
    scope: 'openid',
    nonce: generateNonce(),
  });
  return `https://${MIDWAY_HOST}/SSO?${params.toString()}`;
}

/**
 * Build the Midway redirect URL that will come back with ?id_token=...
 */
function buildSSORedirectUrl(): string {
  const params = new URLSearchParams({
    response_type: 'id_token',
    client_id: CLIENT_ID,
    redirect_uri: window.location.href.split('?')[0], // current page without query params
    scope: 'openid',
    nonce: generateNonce(),
  });
  return `https://${MIDWAY_HOST}/SSO/redirect?${params.toString()}`;
}

/**
 * Get Midway token - tries cross-site first, falls back to redirect.
 */
async function getMidwayToken(): Promise<string> {
  // 1. Check if we have a cached valid token
  if (cachedToken && tokenExpiry > Date.now()) {
    return cachedToken;
  }

  // 2. Check URL for token (returning from redirect)
  const urlToken = getTokenFromUrl();
  if (urlToken) {
    cachedToken = urlToken;
    // Midway tokens are valid for 15 minutes
    tokenExpiry = Date.now() + 14 * 60 * 1000;
    return urlToken;
  }

  // 3. Try cross-site fetch (works if cookies are sent cross-origin)
  try {
    const response = await fetch(buildSSOUrl(), { credentials: 'include' });
    if (response.ok) {
      const token = await response.text();
      // Validate it looks like a JWT (starts with eyJ)
      if (token && token.startsWith('eyJ')) {
        cachedToken = token;
        tokenExpiry = Date.now() + 14 * 60 * 1000;
        return token;
      }
    }
  } catch (e) {
    // Cross-site blocked (expected in most browsers due to SameSite cookies)
    console.info('Midway cross-site call failed, using redirect flow');
  }

  // 4. Redirect to Midway - will come back with ?id_token=...
  window.location.assign(buildSSORedirectUrl());
  // This line won't execute - browser navigates away
  throw new Error('Redirecting to Midway for authentication...');
}

/**
 * Call STS AssumeRoleWithWebIdentity with the Midway OIDC token.
 */
async function assumeRoleWithWebIdentity(token: string): Promise<AwsCredentials> {
  const params = new URLSearchParams({
    Action: 'AssumeRoleWithWebIdentity',
    Version: '2011-06-15',
    RoleArn: ROLE_ARN,
    RoleSessionName: 'bmr-web-session',
    WebIdentityToken: token,
    DurationSeconds: '900', // 15 minutes (matches midway token lifetime)
  });

  const response = await fetch(`${STS_ENDPOINT}/?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  if (!response.ok) {
    const text = await response.text();
    console.error('STS AssumeRoleWithWebIdentity failed:', text);
    // If STS fails, token might be invalid - clear cache and retry with redirect
    cachedToken = null;
    tokenExpiry = 0;
    throw new Error(`STS failed: ${response.status}`);
  }

  const xml = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');

  const accessKeyId = doc.querySelector('AccessKeyId')?.textContent;
  const secretAccessKey = doc.querySelector('SecretAccessKey')?.textContent;
  const sessionToken = doc.querySelector('SessionToken')?.textContent;
  const expiration = doc.querySelector('Expiration')?.textContent;

  if (!accessKeyId || !secretAccessKey || !sessionToken) {
    throw new Error('Failed to parse STS response');
  }

  return {
    accessKeyId,
    secretAccessKey,
    sessionToken,
    expiration: new Date(expiration || Date.now() + 900000),
  };
}

/**
 * Get AWS credentials. Caches and auto-refreshes.
 */
export async function getCredentials(): Promise<AwsCredentials> {
  // Return cached if still valid (with 1 min buffer)
  if (cachedCredentials && cachedCredentials.expiration.getTime() > Date.now() + 60000) {
    return cachedCredentials;
  }

  const token = await getMidwayToken();
  cachedCredentials = await assumeRoleWithWebIdentity(token);
  return cachedCredentials;
}

/**
 * Initialize auth on page load - extract token from URL if present.
 * Call this early in app startup.
 */
export function initAuth(): void {
  const urlToken = getTokenFromUrl();
  if (urlToken) {
    cachedToken = urlToken;
    tokenExpiry = Date.now() + 14 * 60 * 1000;
  }
}
